import { ObservationControllerService } from "@9amhealth/openapi";
import { getLocalTimeZone, now } from "@internationalized/date";
import { Cubit } from "blac-next";
import { Observation } from "fhir/r4";
import { LoincCodingCode } from "src/constants/fhir";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { TranslationKey } from "src/types/translationKey";

type Glp1RefillBlocState = {
  loading: boolean;
  hasWeightReading: boolean;
  error?  : TranslationKey;
};

export default class Glp1RefillBloc extends Cubit<Glp1RefillBlocState> {
  static WEIGHT_READING_TIME_RANGE = 72;
  constructor() {
    super({
      loading: false,
      hasWeightReading: false
    });
  }

  /**
   * Checks if the user has a weight reading from today, yesterday, or the day before (last two full days plus today)
   * then updates the state `hasWeightReading`
   */
  checkWeightReading = async () => {
    this.patch({
      loading: true,
      error: undefined
    });
    const weightValues = await this.fetchWeightReadings();
    this.patch({
      loading: false,
      hasWeightReading: weightValues.length > 0
    });
  };

  /**
   * Fetches the weight readings from today, yesterday, and the day before (last two full days plus today)
   * @returns the weight readings
   */
  fetchWeightReadings = async (): Promise<Observation[]> => {
    const currentDate = now(getLocalTimeZone());
    // Get today, yesterday, and the day before as CalendarDate
    const today = currentDate;
    const yesterday = today.subtract({ days: 1 });
    const dayBeforeYesterday = today.subtract({ days: 2 });

    // add one day to the end date to ensure that the last day is included
    const toDateString = today.add({ days: 1 }).toString().split('T')[0];
    const fromDateString = dayBeforeYesterday.toString().split('T')[0];

    try {
      const response = await ObservationControllerService.getObservations(
        fromDateString,
        toDateString,
        [LoincCodingCode.weight]
      );
      const { data } = response as unknown as {
        status: string;
        data: Observation[];
      };
      // Only keep readings whose date is in [dayBeforeYesterday, yesterday, today]
      const validDates = [
        dayBeforeYesterday.toString().split('T')[0],
        yesterday.toString().split('T')[0],
        today.toString().split('T')[0]
      ];
      const dataInDateRange = data.filter((entry) => {
        if (!entry.effectiveDateTime) {
          return false;
        }
        // Extract just the date part (YYYY-MM-DD)
        const datePart = entry.effectiveDateTime.split("T")[0];
        return validDates.includes(datePart);
      });
      return dataInDateRange;
    } catch (e) {
      this.patch({
        error: 'error.fetchingWeightReadings'
      });
      if (e instanceof Error) {
        reportErrorSentry(e);
      }
    }

    return [];
  };
}
